/**
 * Canvas renderer — draw loop wiring projection + viewport + polygon layer.
 *
 * Responsibilities:
 *   - Manage the <canvas> element lifecycle (HiDPI resize via ResizeObserver)
 *   - Render the bounding box border
 *   - Draw completed polygons with fill, stroke, and selection handles
 *   - Draw in-progress polygon preview (dashed outline, vertex dots, rubber-band line)
 *   - Export `render` so other modules can trigger redraws
 *   - Export `setDrawingState` so the draw controller can feed preview data
 */

import { latLngToPixel, polygonAreaM2 } from "./projection.js";
import { applyTransform, resetTransform, getViewport } from "./viewport.js";
import { getPolygons, getSelectedId } from "./polygons.js";
import { contrastTextColor } from "../ui/brushColor.js";
import { hasUnderlay, isUnderlayVisible, getUnderlayImage, getUnderlayScale, computeUnderlayDrawParams } from "./underlay.js";

/** @type {HTMLCanvasElement} */
let canvas = null;
/** @type {CanvasRenderingContext2D} */
let ctx = null;
/** @type {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} */
let currentBbox = null;
/** @type {{ width: number, height: number }} — logical (CSS pixel) canvas size */
let logicalSize = { width: 0, height: 0 };
/** devicePixelRatio captured at init time */
let dpr = 1;

/** ResizeObserver so we stay crisp when the window resizes */
let resizeObserver = null;

/**
 * In-progress drawing preview state.
 * Null when not drawing; otherwise an object with vertices and optional cursorPos.
 *
 * @type {{ vertices: Array<{lat: number, lng: number}>, cursorPos: {canvasX: number, canvasY: number}|null, closingWouldIntersect?: boolean }|null}
 */
let drawingState = null;

/**
 * Hover state for vertex handle highlighting.
 * vertexIndex: index of the hovered vertex, or null
 * polygonId: id of the polygon whose vertex is hovered, or null
 * edgeHover: true when cursor is near an edge (no specific vertex hovered)
 */
let hoverState = { vertexIndex: null, polygonId: null, edgeHover: false };

/**
 * Selected vertex state for highlighting the actively selected vertex.
 * index: index of the selected vertex, or null
 * polygonId: id of the polygon whose vertex is selected, or null
 */
let selectedVertexState = { index: null, polygonId: null };

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the canvas with a given bounding box.
 *
 * Should be called once after the user confirms a bbox.
 * Safe to call again to reinitialise with a new bbox.
 *
 * @param {HTMLCanvasElement} canvasEl
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 */
export function initCanvas(canvasEl, bbox) {
  canvas = canvasEl;
  ctx = canvas.getContext("2d");
  currentBbox = bbox;
  dpr = window.devicePixelRatio || 1;

  // Tear down any existing observer before creating a new one
  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  const container = document.getElementById("canvas-container");
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      _resizeCanvas(width, height);
    }
  });
  resizeObserver.observe(container);

  // Trigger an initial size pass
  const rect = container.getBoundingClientRect();
  _resizeCanvas(rect.width, rect.height);
}

/**
 * Draw the current frame.
 *
 * Order:
 *   1. Clear (identity transform)
 *   2. Apply viewport transform (DPR x zoom/pan)
 *   3. Draw bounding box border
 *   4. Draw polygon layer (completed polygons + drawing preview)
 */
export function render() {
  if (!canvas || !ctx || !currentBbox) return;

  const { scale, offsetX, offsetY } = getViewport();

  // 1. Clear with identity
  resetTransform(ctx);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 2. Apply combined DPR + viewport transform
  applyTransform(ctx, dpr);

  // 3. Bounding box border — full logical canvas area
  ctx.save();
  ctx.strokeStyle = "#cccccc";
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, logicalSize.width, logicalSize.height);
  ctx.restore();

  // 3.5 Underlay image (if loaded and visible)
  if (hasUnderlay() && isUnderlayVisible()) {
    const img = getUnderlayImage();
    const { x, y, w, h } = computeUnderlayDrawParams(
      img.naturalWidth, img.naturalHeight, currentBbox, logicalSize, getUnderlayScale()
    );
    ctx.drawImage(img, x, y, w, h);
  }

  // 4. Polygon layer (completed polygons + in-progress preview)
  drawPolygonLayer();
}

/**
 * Set the in-progress drawing state for live preview rendering.
 * Pass null to clear the drawing preview.
 *
 * @param {{ vertices: Array<{lat: number, lng: number}>, cursorPos: {canvasX: number, canvasY: number}|null }|null} state
 */
export function setDrawingState(state) {
  drawingState = state;
}

/**
 * Return the current logical (CSS pixel) canvas size.
 * Used by application.js for pixelToLatLng conversions.
 *
 * @returns {{ width: number, height: number }}
 */
export function getLogicalSize() {
  return { ...logicalSize };
}

/**
 * Update hover state for vertex handle highlighting.
 * Merges the provided fields into the current hoverState.
 *
 * @param {{ vertexIndex?: number|null, polygonId?: string|null, edgeHover?: boolean }} state
 */
export function setHoverState(state) {
  hoverState = { ...hoverState, ...state };
}

/**
 * Update selected vertex state for rendering the actively selected vertex.
 *
 * @param {{ index: number|null, polygonId: string|null }} state
 */
export function setSelectedVertexState(state) {
  selectedVertexState = { ...state };
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Resize the canvas to match the container at the correct physical resolution.
 *
 * @param {number} cssWidth
 * @param {number} cssHeight
 */
function _resizeCanvas(cssWidth, cssHeight) {
  dpr = window.devicePixelRatio || 1;
  logicalSize = { width: cssWidth, height: cssHeight };

  canvas.width = cssWidth * dpr;
  canvas.height = cssHeight * dpr;
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;

  render();
}

/**
 * Draw all completed polygons and the in-progress drawing preview.
 *
 * Completed polygon rendering:
 *   - Fill with polygon.fillColor
 *   - Stroke: #333 at 1.5px (unselected) or 3.5px (selected)
 *   - Selected polygon: small 5x5 vertex handles at each vertex
 *
 * In-progress preview rendering:
 *   - Partial polygon shape with translucent fill and dashed stroke
 *   - Small filled dots (4px radius) at each placed vertex
 *   - First vertex: larger dot (7px) with #4488cc ring (2px stroke)
 *   - Rubber-band dashed line from last vertex to cursor position
 */
function drawPolygonLayer() {
  if (!currentBbox) return;

  const selectedId = getSelectedId();

  // --- Completed polygons (sorted by zIndex ascending: lower zIndex drawn first/behind) ---
  const sorted = [...getPolygons()].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  for (const polygon of sorted) {
    const pixelVerts = polygon.vertices.map((v) =>
      latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
    );

    if (pixelVerts.length < 2) continue;

    ctx.save();

    // Build path
    ctx.beginPath();
    ctx.moveTo(pixelVerts[0].x, pixelVerts[0].y);
    for (let i = 1; i < pixelVerts.length; i++) {
      ctx.lineTo(pixelVerts[i].x, pixelVerts[i].y);
    }
    ctx.closePath();

    // Fill
    ctx.fillStyle = polygon.fillColor;
    ctx.fill();

    // Stroke
    const isSelected = polygon.id === selectedId;
    ctx.strokeStyle = "#333333";
    ctx.lineWidth = isSelected ? 3.5 : 1.5;
    ctx.stroke();

    // Vertex handles for selected polygon — circular with optional hover ring
    if (isSelected) {
      for (let i = 0; i < pixelVerts.length; i++) {
        const pv = pixelVerts[i];

        // Draw filled circle handle (6px radius)
        const isVertexSelected = polygon.id === selectedVertexState.polygonId && i === selectedVertexState.index;
        ctx.beginPath();
        ctx.arc(pv.x, pv.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = isVertexSelected ? "#4488cc" : "#333333";
        ctx.fill();

        // Draw hover ring (8px radius, blue stroke) when this vertex is hovered
        if (polygon.id === hoverState.polygonId && i === hoverState.vertexIndex) {
          ctx.beginPath();
          ctx.arc(pv.x, pv.y, 8, 0, Math.PI * 2);
          ctx.strokeStyle = "#4488cc";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    // Area label at centroid
    const area = polygonAreaM2(polygon.vertices);
    const n = polygon.vertices.length;
    const centroidLat = polygon.vertices.reduce((s, v) => s + v.lat, 0) / n;
    const centroidLng = polygon.vertices.reduce((s, v) => s + v.lng, 0) / n;
    const { x: cx, y: cy } = latLngToPixel(centroidLat, centroidLng, currentBbox, logicalSize);

    const textColor = contrastTextColor(polygon.fillColorHex || "#ffffff");
    ctx.font = "bold 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = textColor;
    ctx.fillText(`${Math.round(area)} m\u00B2`, cx, cy);

    ctx.restore();
  }

  // --- In-progress drawing preview ---
  if (!drawingState || drawingState.vertices.length === 0) return;

  const previewVerts = drawingState.vertices.map((v) =>
    latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
  );

  ctx.save();

  // Partial polygon shape (filled + dashed stroke)
  if (previewVerts.length >= 2) {
    ctx.beginPath();
    ctx.moveTo(previewVerts[0].x, previewVerts[0].y);
    for (let i = 1; i < previewVerts.length; i++) {
      ctx.lineTo(previewVerts[i].x, previewVerts[i].y);
    }
    ctx.closePath();

    ctx.fillStyle = "rgba(68,136,204,0.2)";
    ctx.fill();

    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = "#333333";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Rubber-band line from last vertex to cursor
  if (drawingState.cursorPos && previewVerts.length >= 1) {
    const last = previewVerts[previewVerts.length - 1];
    const { canvasX, canvasY } = drawingState.cursorPos;

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(canvasX, canvasY);
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = drawingState.closingWouldIntersect ? "#cc4444" : "#333333";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Vertex dots
  for (let i = 0; i < previewVerts.length; i++) {
    const pv = previewVerts[i];
    const isFirst = i === 0;

    if (isFirst) {
      // First vertex: larger dot with a highlight ring
      ctx.beginPath();
      ctx.arc(pv.x, pv.y, 7, 0, Math.PI * 2);
      ctx.fillStyle = "#333333";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(pv.x, pv.y, 7, 0, Math.PI * 2);
      ctx.strokeStyle = "#4488cc";
      ctx.lineWidth = 2;
      ctx.stroke();
    } else {
      // Regular vertex dot
      ctx.beginPath();
      ctx.arc(pv.x, pv.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#333333";
      ctx.fill();
    }
  }

  ctx.restore();
}
